import { Spacing } from '@persona-id/design-tokens';
import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import { WorkflowError } from 'dashboard/models/workflow';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { TransitionDuration } from 'design-system/variables/transitions';
import ImageCircleAlert from 'images/dashboard/circle-alert.svg';
import useToggle from 'lib/hooks/useToggle';

const Container = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

const ErrorSummary = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  height: ${({ theme }) => theme.button.height.md};
  border-radius: ${({ theme }) => theme.button.borderRadius};
  color: ${({ theme }) => theme.color.variants.danger.default};
  size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  padding: 0 ${Spacing[2]};
  transition: ${TransitionDuration.Fast};
  white-space: nowrap;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.color.variants.danger.highlightLight};
  }

  & svg {
    margin-left: ${Spacing[2]};
    & path {
      stroke: ${({ theme }) => theme.color.variants.danger.default};
    }
  }
`;

const ErrorContent = styled(DropdownMenu.Item)`
  width: 240px;
  word-break: break-word;
`;

interface WorkflowValidationErrorsDropdownProps {
  validationErrors: WorkflowError[];
  onClickError: (index: number) => void;
}

const WorkflowValidationErrorsDropdown: VFC<WorkflowValidationErrorsDropdownProps> = ({
  validationErrors,
  onClickError,
}) => {
  const [dropdownOpened, { enable: openDropdown, disable: hideDropdown }] = useToggle(false);
  const errorItems = useMemo(
    () =>
      validationErrors.map((error, i) => ({
        id: `workflow-validation-errors-dropdown__error-item-${i}`,
        name: `workflow-validation-errors-dropdown__error-item-${i}`,
        onClick: () => onClickError(i),
        label: error.message,
      })),
    [validationErrors, onClickError],
  );

  return (
    <Container>
      <DropdownMenu
        align='center'
        contentProps={{ onEscapeKeyDown: hideDropdown, onInteractOutside: hideDropdown }}
        open={dropdownOpened}
        onOpenChange={(opened) => opened && openDropdown()}
        trigger={
          <ErrorSummary
            id='workflow-validation-errors-dropdwon__total'
            aria-label='Total errors'
            tabIndex={0}
            onMouseEnter={openDropdown}
          >
            <p>
              View {validationErrors.length} {validationErrors.length > 1 ? 'errors' : 'error'}
            </p>
            <ImageCircleAlert height={16} width={16} />
          </ErrorSummary>
        }
      >
        {errorItems.map((item) => (
          <ErrorContent id={item.id} onClick={item.onClick} key={item.id}>
            {item.label}
          </ErrorContent>
        ))}
      </DropdownMenu>
    </Container>
  );
};

export default WorkflowValidationErrorsDropdown;
